"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import resourceGrantApi from "@/api/resource-grants";
import userApi from "@/api/user";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  ResourceGrant,
  ResourceGrantAction,
  ResourceGrantEffect,
} from "@/interfaces/resource-grant.interface";
import { User } from "@/interfaces/user.interface";
import { useToastStore } from "@/stores/toast.store";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { PrimeReactProvider } from "primereact/api";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { Tooltip } from "primereact/tooltip";
import { MultiSelect } from "primereact/multiselect";
import { ChevronDown, Crown, Link2, Users, ShieldX } from "lucide-react";

export interface RelatedGrantResource {
  resourceEndpoint: string;
  resourceType: string;
  resourceId: string;
  name: string;
  grantable?: boolean;
}

interface ResourceGrantDialogProps {
  visible: boolean;
  resourceEndpoint: string;
  resourceType: string;
  resourceId?: string;
  resourceName?: string;
  ownerId?: string;
  relatedResourceLabel?: string;
  loadRelatedResources?: (resourceId: string) => Promise<RelatedGrantResource[]>;
  onHide: () => void;
}

interface GrantTarget {
  resourceEndpoint: string;
  resourceType: string;
  resourceId: string;
  name: string;
  grantable: boolean;
}

type AccessLevel = "view" | "edit" | "full";
type UserAccess = {
  userId: string;
  grants: ResourceGrant[];
};

const ACCESS_ACTIONS: Record<AccessLevel, ResourceGrantAction[]> = {
  view: ["read"],
  edit: ["read", "update"],
  full: ["read", "update", "delete"],
};

const GRANT_ACTIONS: ResourceGrantAction[] = ["read", "update", "delete"];

const ACCESS_LABELS: Record<AccessLevel, string> = {
  view: "Viewer",
  edit: "Editor",
  full: "Full access",
};

const ACCESS_OPTIONS = Object.entries(ACCESS_LABELS).map(([value, label]) => ({
  label,
  value: value as AccessLevel,
}));

const actionClasses: Record<ResourceGrantAction, string> = {
  read: "border-sky-300 bg-sky-100 text-sky-800",
  update: "border-violet-300 bg-violet-100 text-violet-800",
  delete: "border-rose-300 bg-rose-100 text-rose-800",
};

const chipClass = "inline-flex rounded-md border px-2.5 py-1 text-xs font-semibold capitalize";


const nameInitials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const accessLevelOf = (grants: ResourceGrant[]): AccessLevel => {
  const allowed = new Set(
    grants
      .filter((grant) => grant.effect === "allow")
      .map((grant) => grant.action),
  );
  if (allowed.has("delete")) return "full";
  if (allowed.has("update")) return "edit";
  return "view";
};

export default function ResourceGrantDialog({
  visible,
  resourceEndpoint,
  resourceType,
  resourceId,
  resourceName,
  ownerId,
  relatedResourceLabel = "related resources",
  loadRelatedResources,
  onHide,
}: ResourceGrantDialogProps) {
  const showToast = useToastStore((s) => s.showToast);
  const [grants, setGrants] = useState<ResourceGrant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [accessLevel, setAccessLevel] = useState<AccessLevel>("view");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removingKey, setRemovingKey] = useState("");
  const [relatedResources, setRelatedResources] = useState<RelatedGrantResource[]>([]);
  const [selectedRelatedIds, setSelectedRelatedIds] = useState<string[]>([]);
  const [relatedOpen, setRelatedOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [ownerUser, setOwnerUser] = useState<User | null>(null);

  const currentUser = useCurrentUser();
  const isSuperAdmin = currentUser?.isSuperAdmin === true;
  const isOwner = currentUser?._id === ownerId;
  const hasAllScope =
    (currentUser?.permissionCodes?.includes(`${resourceType}:read:all`) ?? false) &&
    (currentUser?.permissionCodes?.includes(`${resourceType}:update:all`) ?? false);
  const canManageDeny = isSuperAdmin || isOwner || hasAllScope;
  const denyMenuRef = useRef<Menu>(null);
  const usersById = useMemo(
    () => Object.fromEntries(users.map((user) => [user._id, user])),
    [users],
  );

  const userOptions = useMemo(
    () =>
      users
        .filter((user) => user._id !== currentUser?._id && user._id !== ownerId)
        .map((user) => ({
          label: `${user.userName || user.email} (${user.email})`,
          value: user._id,
        })),
    [currentUser?._id, ownerId, users],
  );

  const targetResources = useMemo(
    (): GrantTarget[] =>
      resourceId
        ? [
            {
              resourceEndpoint,
              resourceType,
              resourceId,
              name: resourceName || resourceType,
              grantable: true,
            },
            ...relatedResources.map((resource) => ({
              ...resource,
              grantable: resource.grantable ?? true,
            })),
          ]
        : [],
    [resourceType, relatedResources, resourceEndpoint, resourceId, resourceName],
  );

  const primaryTarget = targetResources[0];
  const selectedRelatedTargets = targetResources.filter(
    (resource, index) =>
      index > 0 &&
      resource.grantable &&
      selectedRelatedIds.includes(resource.resourceId),
  );
  const selectedTargets = primaryTarget ? [primaryTarget, ...selectedRelatedTargets] : [];
  const grantableRelatedResources = relatedResources.filter((resource) => resource.grantable !== false);
  const allRelatedSelected =
    grantableRelatedResources.length > 0 &&
    selectedRelatedIds.length === grantableRelatedResources.length;

  const primaryGrants = useMemo(
    () =>
      grants.filter(
        (grant) =>
          grant.resourceType === resourceType &&
          grant.resourceId === resourceId,
      ),
    [grants, resourceType, resourceId],
  );

  const sharedUsers = useMemo(() => {
    const grouped = new Map<string, ResourceGrant[]>();
    primaryGrants
      .filter((grant) => grant.effect === "allow")
      .forEach((grant) => {
        grouped.set(grant.userId, [...(grouped.get(grant.userId) || []), grant]);
      });
    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([userId, userGrants]): UserAccess => ({ userId, grants: userGrants }));
  }, [primaryGrants]);

  const denyUsers = useMemo(() => {
    const map = new Map<string, Set<ResourceGrantAction>>();
    grants
      .filter((grant) => grant.effect === "deny")
      .forEach((grant) => {
        if (!map.has(grant.userId)) map.set(grant.userId, new Set());
        map.get(grant.userId)!.add(grant.action);
      });
    return Array.from(map.entries()).map(([userId, actions]) => ({
      userId,
      actions: Array.from(actions),
    }));
  }, [grants]);

  const loadData = async () => {
    if (!resourceId) return;
    setLoading(true);
    try {
      const [rawResources, userList, ownerResponse] = await Promise.all([
        loadRelatedResources ? loadRelatedResources(resourceId) : Promise.resolve([]),
        resourceGrantApi.getAssignableUsers(),
        ownerId ? userApi.getOneById(ownerId) : Promise.resolve(null),
      ]);
      const resources = rawResources.map((r) => ({ ...r, grantable: r.grantable ?? true }));
      const grantableResources = resources.filter((r) => r.grantable);
      const grantableTargets = [
        { resourceType: resourceEndpoint, resourceId },
        ...grantableResources.map((r) => ({ resourceType: r.resourceEndpoint, resourceId: r.resourceId })),
      ];
      const allGrants = await resourceGrantApi.bulkRead(grantableTargets);
      setGrants(allGrants);
      setOwnerUser(ownerResponse?.data ?? null);
      setUsers(userList);
      setRelatedResources(resources);
      setSelectedRelatedIds(grantableResources.map((r) => r.resourceId));
    } catch {
      showToast("error", "Error", "Failed to load sharing settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visible) return;
    setSelectedUserIds([]);
    setAccessLevel("view");
    setRelatedResources([]);
    setSelectedRelatedIds([]);
    setRelatedOpen(false);
    setAdvancedOpen(false);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, resourceEndpoint, resourceId]);

  const applyAccess = async (
    userIds: string[],
    actions: ResourceGrantAction[],
    effect: ResourceGrantEffect,
    targets = selectedTargets,
  ) => {
    if (userIds.length === 0 || actions.length === 0 || targets.length === 0) return;
    setSaving(true);
    try {
      const operations = targets.flatMap((target) =>
        userIds.flatMap((userId) =>
          actions.map((action) => ({
            resourceType: target.resourceEndpoint,
            resourceId: target.resourceId,
            granteeId: userId,
            action,
            effect,
          })),
        ),
      );
      await resourceGrantApi.bulkApply(operations);
      showToast("success", "Success", "Sharing settings updated");
      await loadData();
      setSelectedUserIds([]);
    } catch {
      showToast("error", "Error", "Failed to update sharing settings");
    } finally {
      setSaving(false);
    }
  };

  const removeUserAccess = async (access: UserAccess) => {
    if (!primaryTarget) return;
    setRemovingKey(access.userId);
    try {
      await resourceGrantApi.bulkRevoke(
        targetResources.map((target) => ({
          resourceType: target.resourceEndpoint,
          resourceId: target.resourceId,
          granteeId: access.userId,
        })),
      );
      showToast("success", "Success", "Access removed");
      await loadData();
    } catch {
      showToast("error", "Error", "Failed to remove access");
    } finally {
      setRemovingKey("");
    }
  };

  const updateUserAccess = async (access: UserAccess, nextLevel: AccessLevel) => {
    if (!primaryTarget) return;
    setRemovingKey(access.userId);
    try {
      await resourceGrantApi.bulkRevoke(
        targetResources.map((target) => ({
          resourceType: target.resourceEndpoint,
          resourceId: target.resourceId,
          granteeId: access.userId,
        })),
      );
      await resourceGrantApi.bulkApply(
        targetResources.flatMap((target) =>
          ACCESS_ACTIONS[nextLevel].map((action) => ({
            resourceType: target.resourceEndpoint,
            resourceId: target.resourceId,
            granteeId: access.userId,
            action,
            effect: "allow" as ResourceGrantEffect,
          })),
        ),
      );
      await loadData();
    } catch {
      showToast("error", "Error", "Failed to update access");
    } finally {
      setRemovingKey("");
    }
  };

  const revokeDenyAccess = async (userId: string) => {
    const key = `deny:${userId}`;
    setRemovingKey(key);
    try {
      await resourceGrantApi.bulkRevoke(
        targetResources.map((target) => ({
          resourceType: target.resourceEndpoint,
          resourceId: target.resourceId,
          granteeId: userId,
        })),
      );
      await loadData();
    } catch {
      showToast("error", "Error", "Failed to revoke deny");
    } finally {
      setRemovingKey("");
    }
  };

  const toggleRelatedResource = (resourceIdToToggle: string) => {
    setSelectedRelatedIds((prev) =>
      prev.includes(resourceIdToToggle)
        ? prev.filter((id) => id !== resourceIdToToggle)
        : [...prev, resourceIdToToggle],
    );
  };

  const ownerName = ownerUser?.userName || "Unknown user";
  const ownerEmail = ownerUser?.email || "unknown email";
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => showToast("success", "", "Copied!"));
  };

  const dialogHeader = (
    <div className="flex flex-col gap-0.5">
      <span>{resourceName ? `Share "${resourceName}"` : "Share"}</span>
      {resourceId && (
        <span className="flex items-center gap-1.5">
          <span className="font-mono text-[11px] font-normal text-gray-400">{resourceId}</span>
          <button
            type="button"
            onClick={() => copyToClipboard(resourceId)}
            className="flex items-center text-gray-300 transition hover:text-gray-500"
            title="Copy ID"
          >
            <i className="pi pi-copy text-[11px]" />
          </button>
        </span>
      )}
    </div>
  );

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      onHide={onHide}
      style={{ width: "760px", maxWidth: "96vw" }}
      breakpoints={{ "768px": "94vw" }}
      contentStyle={{ paddingTop: 12 }}
    >
      <div className="flex max-h-[78vh] flex-col gap-4 overflow-auto">
        {/* Add people */}
        <section className="flex flex-wrap items-center gap-2">
          <MultiSelect
            value={selectedUserIds}
            options={userOptions}
            onChange={(event) => setSelectedUserIds(event.value)}
            placeholder="Add people"
            filter
            display="chip"
            className="min-w-0 flex-1"
            disabled={loading || saving}
            maxSelectedLabels={3}
          />
          <Dropdown
            value={accessLevel}
            options={ACCESS_OPTIONS}
            onChange={(event) => setAccessLevel(event.value)}
            className="w-44 shrink-0"
            disabled={loading || saving}
          />
        </section>

        {/* Related resources collapsible */}
        {loadRelatedResources && (
          <div className="rounded-lg border border-gray-200">
            <div
              className="flex cursor-pointer select-none items-center gap-3 bg-gray-50 px-4 py-3 hover:bg-gray-100"
              onClick={() => setRelatedOpen((v) => !v)}
            >
              <ChevronDown
                size={15}
                className={`shrink-0 text-gray-400 transition-transform duration-200 ${relatedOpen ? "rotate-0" : "-rotate-90"}`}
              />
              <span className="flex flex-1 items-center gap-2">
                <Link2 size={14} className="text-gray-400" />
                <span className="text-sm font-semibold text-gray-800">
                  Also share {relatedResourceLabel}
                </span>
              </span>
              <span className={`rounded-md px-2 py-0.5 text-xs font-semibold tabular-nums ${
                selectedRelatedIds.length > 0 ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-400"
              }`}>
                {selectedRelatedIds.length}/{grantableRelatedResources.length}
              </span>
            </div>
            <div className={`grid transition-all duration-300 ease-in-out ${relatedOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
              <div className="overflow-hidden rounded-b-lg border-t border-gray-100">
                <div className="flex justify-end gap-2 px-3 pt-2">
                  <Button
                    label="Select all"
                    size="small"
                    text
                    disabled={loading || grantableRelatedResources.length === 0 || allRelatedSelected}
                    onClick={() =>
                      setSelectedRelatedIds(
                        grantableRelatedResources.map((resource) => resource.resourceId),
                      )
                    }
                  />
                  <Button
                    label="Clear"
                    size="small"
                    text
                    severity="secondary"
                    disabled={loading || selectedRelatedIds.length === 0}
                    onClick={() => setSelectedRelatedIds([])}
                  />
                </div>
                <div className="max-h-52 overflow-auto p-3">
                  {loading ? (
                    <div className="flex h-24 items-center justify-center text-sm text-gray-500">
                      Loading {relatedResourceLabel}...
                    </div>
                  ) : relatedResources.length ? (
                    <div className="grid grid-cols-1 gap-2">
                      {relatedResources.map((resource) => {
                        const selected = selectedRelatedIds.includes(resource.resourceId);
                        const disabled = resource.grantable === false;
                        return (
                          <button
                            key={resource.resourceId}
                            type="button"
                            disabled={disabled}
                            onClick={() => toggleRelatedResource(resource.resourceId)}
                            className={`flex min-h-11 items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition ${
                              selected
                                ? "border-indigo-300 bg-indigo-50"
                                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                            } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-gray-700">{resource.name}</span>
                              <span className="flex items-center gap-1">
                                <span className="font-mono text-[10px] text-gray-400">{resource.resourceId}</span>
                                <span
                                  role="button"
                                  onClick={(e) => { e.stopPropagation(); copyToClipboard(resource.resourceId); }}
                                  className="flex cursor-pointer items-center text-gray-300 transition hover:text-gray-500"
                                  title="Copy ID"
                                >
                                  <i className="pi pi-copy text-[10px]" />
                                </span>
                              </span>
                            </span>
                            <span className="inline-flex shrink-0 items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-600">
                              {resource.resourceType}
                            </span>
                            <i className={`pi ${selected ? "pi-check-circle text-indigo-600" : "pi-circle text-gray-300"}`} />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex h-24 items-center justify-center text-sm text-gray-500">
                      No {relatedResourceLabel}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-gray-200" />

        {/* People with access */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={15} className="text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-700">People with access</h3>
            </div>
            <Button icon="pi pi-refresh" text size="small" onClick={loadData} disabled={loading || saving} />
          </div>

          <div className="flex flex-col gap-2">
            {/* Owner gradient card */}
            <div className="flex items-center gap-4 rounded-xl border border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50 px-5 py-4">
              <div className="flex h-10 w-10 shrink-0 select-none items-center justify-center rounded-full bg-emerald-500 text-sm font-bold text-white">
                {nameInitials(ownerName)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-sm font-bold text-gray-900">
                  <span className="truncate">{ownerName}</span>
                  {ownerId === currentUser?._id && (
                    <span className="shrink-0 text-xs font-normal text-gray-400">(you)</span>
                  )}
                </div>
                <div className="truncate text-xs text-gray-500">{ownerEmail}</div>
                {ownerId && (
                  <div className="flex items-center gap-0.5">
                    <span className="font-mono text-[10px] text-gray-400">{ownerId}</span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(ownerId)}
                      className="flex items-center text-gray-300 transition hover:text-gray-500"
                      title="Copy user ID"
                    >
                      <i className="pi pi-copy text-[10px]" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-600">
                  <Crown size={11} />
                  Owner
                </span>
                {canManageDeny && ownerId && ownerId !== currentUser?._id && (
                  <PrimeReactProvider>
                    <Tooltip target=".deny-owner-btn" content="Restrict owner access" position="top" />
                    <Menu
                      ref={denyMenuRef}
                      popup
                      model={[
                        { label: "Deny read", icon: "pi pi-eye-slash", command: () => applyAccess([ownerId], ["read"], "deny", targetResources) },
                        { label: "Deny edit", icon: "pi pi-pencil", command: () => applyAccess([ownerId], ["update"], "deny", targetResources) },
                        { label: "Deny all", icon: "pi pi-ban", command: () => applyAccess([ownerId], GRANT_ACTIONS, "deny", targetResources) },
                      ] satisfies MenuItem[]}
                    />
                    <button
                      type="button"
                      onClick={(e) => denyMenuRef.current?.toggle(e)}
                      className="deny-owner-btn flex h-7 w-7 items-center justify-center rounded-full border border-red-200 text-red-500 transition hover:border-red-300 hover:bg-red-50"
                    >
                      <i className="pi pi-lock text-sm" />
                    </button>
                  </PrimeReactProvider>
                )}
              </div>
            </div>

            {/* Shared users */}
            {loading ? (
              <div className="flex h-28 items-center justify-center text-sm text-gray-500">
                <i className="pi pi-spin pi-spinner mr-2" />
                Loading...
              </div>
            ) : sharedUsers.length ? (
              <div className="rounded-lg border border-gray-200">
                {sharedUsers.map((access) => {
                  const user = usersById[access.userId];
                  const name = user?.userName || "Unknown user";
                  const email = user?.email || "unknown email";
                  const level = accessLevelOf(access.grants);
                  return (
                    <div
                      key={access.userId}
                      className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0"
                    >
                      <div className="flex h-9 w-9 shrink-0 select-none items-center justify-center rounded-full bg-indigo-500 text-xs font-bold text-white">
                        {nameInitials(name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 text-sm font-semibold text-gray-800">
                          <span className="truncate">{name}</span>
                          {access.userId === currentUser?._id && (
                            <span className="shrink-0 text-xs font-normal text-gray-400">(you)</span>
                          )}
                        </div>
                        <div className="truncate text-xs text-gray-400">{email}</div>
                        <div className="flex items-center gap-0.5">
                          <span className="font-mono text-[10px] text-gray-300">{access.userId}</span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(access.userId)}
                            className="flex items-center text-gray-300 transition hover:text-gray-500"
                            title="Copy user ID"
                          >
                            <i className="pi pi-copy text-[10px]" />
                          </button>
                        </div>
                      </div>
                      <Dropdown
                        value={level}
                        options={ACCESS_OPTIONS}
                        onChange={(event) => updateUserAccess(access, event.value)}
                        className="w-44 shrink-0"
                        disabled={saving || removingKey === access.userId}
                      />
                      <Button
                        icon="pi pi-times"
                        text
                        severity="secondary"
                        size="small"
                        loading={removingKey === access.userId}
                        onClick={() => removeUserAccess(access)}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-400">
                Only the owner has access
              </div>
            )}
          </div>
        </section>

        {/* Advanced deny */}
        {canManageDeny && (
          <div className="rounded-lg border border-red-100">
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="flex w-full items-center gap-3 rounded-t-lg bg-red-50/50 px-4 py-3 text-left hover:bg-red-50"
            >
              <ChevronDown
                size={15}
                className={`shrink-0 text-red-300 transition-transform duration-200 ${advancedOpen ? "rotate-0" : "-rotate-90"}`}
              />
              <ShieldX size={14} className="text-red-400" />
              <span className="flex-1 text-sm font-semibold text-gray-800">Advanced deny</span>
              {denyUsers.length > 0 && (
                <span className="rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-red-600">
                  {denyUsers.length}
                </span>
              )}
            </button>
            <div className={`grid transition-all duration-300 ease-in-out ${advancedOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
              <div className="overflow-hidden rounded-b-lg border-t border-red-100">
                <div className="p-4">
                  {denyUsers.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      {denyUsers.map(({ userId, actions }) => {
                        const user = usersById[userId];
                        const key = `deny:${userId}`;
                        return (
                          <div key={userId} className="flex items-center justify-between rounded-lg border border-red-100 bg-red-50 px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 shrink-0 select-none items-center justify-center rounded-full bg-red-200 text-xs font-bold text-red-700">
                                {nameInitials(user?.userName || "Unknown user")}
                              </div>
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-red-800">
                                  {user?.userName || "Unknown user"}
                                </div>
                                <div className="truncate text-xs text-red-400">{user?.email || "unknown email"}</div>
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              {actions.map((action) => (
                                <span key={action} className={`${chipClass} ${actionClasses[action]}`}>{action}</span>
                              ))}
                              <Button
                                icon="pi pi-times"
                                text
                                severity="danger"
                                size="small"
                                loading={removingKey === key}
                                onClick={() => revokeDenyAccess(userId)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-2 text-center text-sm text-gray-400">No deny rules</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 pt-2">
          <span className="text-xs text-gray-400">
            {sharedUsers.length > 0
              ? `${sharedUsers.length} user${sharedUsers.length !== 1 ? "s" : ""} have access`
              : "No shared users yet"}
          </span>
          <div className="flex gap-2">
            {selectedUserIds.length > 0 ? (
              <Button
                label="Share"
                icon="pi pi-send"
                loading={saving}
                disabled={loading}
                onClick={async () => {
                  await applyAccess(selectedUserIds, ACCESS_ACTIONS[accessLevel], "allow");
                  onHide();
                }}
              />
            ) : (
              <Button label="Done" severity="secondary" onClick={onHide} />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
